/**
 * Move one Files structure group onto another project: proposal structures,
 * the group's own file tracks, and the comments, markup, and measurements
 * on those files. Whole-project tracks stay on the source.
 */

import {
  collection,
  deleteField,
  doc,
  getDoc,
  getDocs,
  query,
  Timestamp,
  where,
  writeBatch,
  type DocumentData,
  type DocumentReference,
  type UpdateData,
} from 'firebase/firestore';
import type { ProjectFile, ProjectFileStream, ProposalBuilderData, ProposalNodeSet } from '../types';
import { DEFAULT_PROPOSAL_CHECKLIST } from '../types';
import { db } from './firebase';
import { planMoveStructureGroup } from './moveStructureGroup';
import {
  calculateTotals,
  designIncludedInKitForNewProposalWrite,
  getEffectiveProjectOverheadPercent,
  getProposalDiscountAmount,
} from './proposalCalculations';
import { removeUndefinedDeep } from './removeUndefinedDeep';

const FILES = 'projectFiles';
const STREAMS = 'projectFileStreams';
const PROPOSALS = 'proposalBuilders';
const COMMENT_COLLECTIONS = [
  'pdfAnnotations',
  'documentMarkup',
  'pdfMeasurements',
  'pdfMeasurementCalibrations',
] as const;

export interface MoveStructureGroupProposalSeed {
  clientName?: string;
  projectName?: string;
  location?: string;
  clientLabel?: string;
  descriptor?: string;
  projectNumber?: string;
  profitMargin?: number;
}

export async function moveStructureGroupToProject(input: {
  sourceProjectId: string;
  sourceDealId: string;
  destinationProjectId: string;
  destinationDealId: string;
  groupKey: string;
  streams: ProjectFileStream[];
  files: ProjectFile[];
  createdBy: string;
  proposalSeed?: MoveStructureGroupProposalSeed;
}): Promise<void> {
  if (input.destinationProjectId === input.sourceProjectId) {
    throw new Error('Choose a different project.');
  }
  if (input.destinationDealId === input.sourceDealId) {
    throw new Error('That project shares this proposal. Choose a project with its own proposal.');
  }

  const [sourceSnap, destSnap] = await Promise.all([
    getDoc(doc(db, PROPOSALS, input.sourceDealId)),
    getDoc(doc(db, PROPOSALS, input.destinationDealId)),
  ]);
  const sourceProposal = sourceSnap.exists()
    ? ({ id: sourceSnap.id, ...sourceSnap.data() } as ProposalBuilderData)
    : null;
  const destProposal = destSnap.exists()
    ? ({ id: destSnap.id, ...destSnap.data() } as ProposalBuilderData)
    : null;

  const plan = planMoveStructureGroup({
    groupKey: input.groupKey,
    streams: input.streams,
    files: input.files,
    sourceNodeSets: sourceProposal?.nodeSets ?? [],
    destinationNodeSets: destProposal?.nodeSets ?? [],
  });

  const now = Timestamp.now();
  if (plan.movedNodeSets.length > 0) {
    const sourceTotals = totalsFields(plan.sourceNodeSets, sourceProposal);
    const destBasis: Partial<ProposalBuilderData> = destProposal ?? {
      profitMargin: input.proposalSeed?.profitMargin ?? sourceProposal?.profitMargin ?? 40,
      status: 'draft',
    };
    const destTotals = totalsFields(plan.destinationNodeSets, destBasis);

    const proposalBatch = writeBatch(db);
    if (sourceProposal) {
      proposalBatch.update(doc(db, PROPOSALS, input.sourceDealId), {
        nodeSets: removeUndefinedDeep(plan.sourceNodeSets),
        ...sourceTotals,
        updatedAt: now,
      });
    }
    if (destProposal) {
      proposalBatch.update(doc(db, PROPOSALS, input.destinationDealId), {
        nodeSets: removeUndefinedDeep(plan.destinationNodeSets),
        ...destTotals,
        updatedAt: now,
      });
    } else {
      const seed = input.proposalSeed ?? {};
      const clientName = (seed.clientName || sourceProposal?.clientName || '').trim();
      const projectName = (seed.projectName || seed.descriptor || 'Project').trim();
      proposalBatch.set(doc(db, PROPOSALS, input.destinationDealId), removeUndefinedDeep({
        id: input.destinationDealId,
        dealId: input.destinationDealId,
        clientName,
        projectName,
        location: (seed.location || sourceProposal?.location || '').trim(),
        clientLabel: seed.clientLabel?.trim() || undefined,
        descriptor: seed.descriptor?.trim() || undefined,
        projectNumber: seed.projectNumber?.trim() || undefined,
        slug: proposalSlug(clientName, projectName),
        accessPin: String(Math.floor(1000 + Math.random() * 9000)),
        nodeSets: plan.destinationNodeSets,
        profitMargin: destBasis.profitMargin ?? 40,
        designIncludedInKit: designIncludedInKitForNewProposalWrite(
          sourceProposal?.designIncludedInKit,
        ),
        status: 'draft',
        checklist: { ...DEFAULT_PROPOSAL_CHECKLIST },
        ...destTotals,
        createdAt: now,
        updatedAt: now,
        createdBy: input.createdBy || 'system',
      }));
    }
    await proposalBatch.commit();
  }

  const streamWrites = new Map<string, UpdateData<DocumentData>>();
  for (const update of plan.streamUpdates) {
    streamWrites.set(update.streamId, {
      projectId: input.destinationProjectId,
      structureIds: update.structureIds,
      structureNames: update.structureNames,
      ...(update.clearForkedFrom ? { forkedFromStreamId: deleteField() } : {}),
      updatedAt: now,
    });
  }
  for (const parent of plan.parentForkUpdates) {
    const existing = streamWrites.get(parent.streamId) ?? { updatedAt: now };
    existing.forkedToStreamIds = parent.forkedToStreamIds.length
      ? parent.forkedToStreamIds
      : deleteField();
    streamWrites.set(parent.streamId, existing);
  }

  const writes: Array<{ ref: DocumentReference; data: UpdateData<DocumentData> }> = [];
  streamWrites.forEach((data, streamId) => {
    writes.push({ ref: doc(db, STREAMS, streamId), data });
  });
  for (const file of plan.fileUpdates) {
    const data: UpdateData<DocumentData> = { projectId: input.destinationProjectId };
    if (file.cncStructureIds) {
      data.cncStructureIds = file.cncStructureIds;
      data.cncStructureNames = file.cncStructureNames ?? [];
    }
    writes.push({ ref: doc(db, FILES, file.fileId), data });
  }
  await commitWrites(writes);

  const movedFiles = input.files.filter(file => plan.fileUpdates.some(update => update.fileId === file.id));
  await retargetFileComments(
    movedFiles.map(file => ({ id: file.id!, url: file.url })),
    input.destinationProjectId,
  );
}

function totalsFields(nodeSets: ProposalNodeSet[], proposal: Partial<ProposalBuilderData> | null) {
  const enabled = nodeSets.filter(nodeSet => nodeSet.enabled !== false);
  const totals = calculateTotals(enabled, proposal?.profitMargin ?? 40, {
    globalCostsTotal: proposal?.globalCosts?.totalGlobalCosts ?? 0,
    overheadPercentOfProject: getEffectiveProjectOverheadPercent(proposal?.globalCosts),
    discountAmount: getProposalDiscountAmount(proposal?.discount),
    designEstimating: proposal?.designEstimating,
    proposalStatus: proposal?.status,
  });
  return {
    designTotal: totals.designTotal,
    timberKitTotal: totals.timberKitTotal,
    installTotal: totals.installTotal,
    rawCostTotal: totals.rawCostTotal,
    billableTotal: totals.billableTotal,
    grossProfit: totals.grossProfit,
    structureRawCostTotal: totals.structureRawCostTotal,
    projectOverheadAmount: totals.projectOverheadAmount,
  };
}

function proposalSlug(client: string, project: string): string {
  const slug = `${client}-${project}`
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/-+/g, '-')
    .replace(/^-|-$/g, '');
  return slug || 'proposal';
}

async function commitWrites(writes: Array<{ ref: DocumentReference; data: UpdateData<DocumentData> }>): Promise<void> {
  const chunk = 400;
  for (let i = 0; i < writes.length; i += chunk) {
    const batch = writeBatch(db);
    for (const write of writes.slice(i, i + chunk)) {
      batch.update(write.ref, write.data);
    }
    await batch.commit();
  }
}

async function retargetFileComments(
  files: Array<{ id: string; url?: string }>,
  projectId: string,
): Promise<void> {
  if (files.length === 0) return;
  const refs: DocumentReference[] = [];
  const seen = new Set<string>();
  const remember = (found: DocumentReference) => {
    if (seen.has(found.path)) return;
    seen.add(found.path);
    refs.push(found);
  };

  const jobs: Array<() => Promise<void>> = [];
  for (const file of files) {
    for (const name of COMMENT_COLLECTIONS) {
      jobs.push(async () => {
        const snap = await getDocs(query(collection(db, name), where('documentId', '==', file.id)));
        snap.docs.forEach(row => remember(row.ref));
      });
    }
    if (file.url && file.url !== file.id) {
      jobs.push(async () => {
        const snap = await getDocs(
          query(collection(db, 'pdfAnnotations'), where('documentUrl', '==', file.url)),
        );
        snap.docs.forEach(row => remember(row.ref));
      });
    }
  }
  await runPool(jobs, 8);

  await commitWrites(refs.map(ref => ({ ref, data: { projectId } })));
}

async function runPool(jobs: Array<() => Promise<void>>, limit: number): Promise<void> {
  if (jobs.length === 0) return;
  let next = 0;
  const workers = Array.from({ length: Math.min(limit, jobs.length) }, async () => {
    while (next < jobs.length) {
      const job = jobs[next];
      next += 1;
      await job();
    }
  });
  await Promise.all(workers);
}
